/**
 * Melbourne-area geography for customer reporting (Prompt F). Maps a seeded VIC
 * suburb to one of five compass areas; non-VIC customers group by their own city
 * instead (see `regionLabelFor`).
 *
 * COVERAGE IS ENFORCED for the seed. Every VIC suburb the fixtures seed must
 * appear in `VIC_SUBURB_AREA`: `areaForVicSuburb` throws on an unmapped one.
 * Reporting itself goes through `regionLabelFor`, which counts a blank or
 * unknown suburb (real, staff-typed data) as "Unknown area" (tv-zits). The
 * melbourneAreas test iterates every seeded VIC suburb (auNames.SUBURBS_VIC +
 * the proceduralBookings pools + a fresh buildFixtures) and asserts each one
 * resolves.
 */

export type MelbourneArea = 'north' | 'east' | 'west' | 'south' | 'south-east';

export const MELBOURNE_AREAS: readonly MelbourneArea[] = [
  'north',
  'east',
  'west',
  'south',
  'south-east',
];

export const MELBOURNE_AREA_LABEL: Record<MelbourneArea, string> = {
  north: 'North',
  east: 'East',
  west: 'West',
  south: 'South',
  'south-east': 'South-east',
};

// ASSUMPTION: Geelong is its own city ~75km from Melbourne, but the brief models
// customer geography as five Melbourne compass areas, so the Geelong-region
// suburbs are folded into 'west' (their side of the metro). If the client wants
// a dedicated Geelong bucket, add it to MelbourneArea and remap these five.
export const VIC_SUBURB_AREA: Record<string, MelbourneArea> = {
  // North - inner north + northern corridor
  Coburg: 'north',
  Brunswick: 'north',
  Northcote: 'north',
  Thornbury: 'north',
  Preston: 'north',
  Reservoir: 'north',
  Fitzroy: 'north',
  Carlton: 'north',
  Collingwood: 'north',
  Glenroy: 'north',
  'Pascoe Vale': 'north',
  Essendon: 'north',
  'Moonee Ponds': 'north',
  Melbourne: 'north',
  // East
  Richmond: 'east',
  Hawthorn: 'east',
  Camberwell: 'east',
  'Box Hill': 'east',
  Doncaster: 'east',
  // West - inner west + Geelong region (see ASSUMPTION above)
  Footscray: 'west',
  Yarraville: 'west',
  Williamstown: 'west',
  Geelong: 'west',
  'Geelong West': 'west',
  Newtown: 'west',
  Belmont: 'west',
  'Ocean Grove': 'west',
  // South - inner south
  'St Kilda': 'south',
  Prahran: 'south',
  'South Yarra': 'south',
  Windsor: 'south',
  // South-east - bayside + Mornington Peninsula corridor
  Frankston: 'south-east',
  Seaford: 'south-east',
  Mornington: 'south-east',
  Cheltenham: 'south-east',
};

/**
 * Non-VIC suburb to city. Non-VIC studios group by their own city rather than a
 * Melbourne compass area. Unknown non-VIC suburbs fall back to the state code
 * (never a build error - only the VIC map is coverage-enforced).
 */
export const NON_VIC_SUBURB_CITY: Record<string, string> = {
  // NSW - Central Coast
  Gosford: 'Central Coast',
  Erina: 'Central Coast',
  Wyong: 'Central Coast',
  // NSW - Emu Plains (Penrith / lower Blue Mountains corridor)
  'Emu Plains': 'Penrith',
  Penrith: 'Penrith',
  Blaxland: 'Penrith',
  // NSW - Liverpool (south-west Sydney)
  Liverpool: 'Liverpool',
  Casula: 'Liverpool',
  Moorebank: 'Liverpool',
  // NSW - Sydney (Newtown resolves to Sydney; distinct from VIC Newtown)
  'Surry Hills': 'Sydney',
  Newtown: 'Sydney',
  Redfern: 'Sydney',
  Parramatta: 'Sydney',
  Westmead: 'Sydney',
  'Harris Park': 'Sydney',
  // QLD - Brisbane
  'Fortitude Valley': 'Brisbane',
  'New Farm': 'Brisbane',
  'Spring Hill': 'Brisbane',
  // QLD - Gold Coast
  'Surfers Paradise': 'Gold Coast',
  'Burleigh Heads': 'Gold Coast',
  // QLD - Mackay
  Mackay: 'Mackay',
  'North Mackay': 'Mackay',
  Andergrove: 'Mackay',
  // TAS - Hobart
  Hobart: 'Hobart',
  'Sandy Bay': 'Hobart',
  'Battery Point': 'Hobart',
};

/**
 * Resolve a VIC suburb to its Melbourne compass area. Throws on an unmapped VIC
 * suburb - a missing entry is a build error, not a fallback (the fixtures must
 * only seed suburbs this map covers).
 */
export function areaForVicSuburb(suburb: string): MelbourneArea {
  const area = VIC_SUBURB_AREA[suburb];
  if (!area) {
    throw new Error(
      `melbourneAreas: VIC suburb "${suburb}" is not mapped to an area. Add it to ` +
        `VIC_SUBURB_AREA - a missing VIC suburb is a build error, not a fallback.`,
    );
  }
  return area;
}

/** The VIC map keyed case-insensitively: staff type "coburg" as often as "Coburg". */
const VIC_SUBURB_AREA_CI = new Map(
  Object.entries(VIC_SUBURB_AREA).map(([name, area]) => [name.toLowerCase(), area] as const),
);

/** Where reporting counts a VIC customer whose suburb is blank or not in the map (tv-zits). */
export const UNKNOWN_AREA_LABEL = 'Unknown area';

/**
 * The region label a customer buckets under in geography reporting: a Melbourne
 * compass area for VIC customers, their own city for everyone else. `state` is
 * required because the same suburb name can exist in two states (e.g. Newtown is
 * both a Geelong-VIC suburb and a Sydney-NSW suburb).
 *
 * Real customers are typed in by staff and the public pages, so a VIC customer
 * can have a blank suburb or one the map doesn't know: they count under
 * "Unknown area" rather than throwing on render (tv-zits). The seed's coverage
 * is still enforced through `areaForVicSuburb`.
 */
export function regionLabelFor(suburb: string, state: string): string {
  if (state === 'VIC') {
    const area = VIC_SUBURB_AREA_CI.get((suburb ?? '').trim().toLowerCase());
    return area ? MELBOURNE_AREA_LABEL[area] : UNKNOWN_AREA_LABEL;
  }
  return NON_VIC_SUBURB_CITY[suburb] ?? state;
}
